import { useMemo } from 'react';
import { baixarCSV } from '../utils';
import type { LinhaRelatorio } from '../types';

interface Props {
  eventoNome: string;
  secao: string;
  linhas: LinhaRelatorio[];
  resumoFiltro?: string;
  aoVoltar: () => void;
}

interface PessoaFolha {
  participante: string;
  telefone: string | null;
  participacao: string;
  grupo: string | null;
  respostas: Map<string, string>;
}

export default function FolhaRelatorio({
  eventoNome,
  secao,
  linhas,
  resumoFiltro,
  aoVoltar,
}: Props) {
  const rotuloFiltro = resumoFiltro || 'todos os inscritos';
  const { perguntas, pessoas } = useMemo(() => {
    const ordemMap = new Map<string, number>();
    const perguntasList: string[] = [];
    linhas.forEach((l) => {
      if (!ordemMap.has(l.pergunta)) {
        ordemMap.set(l.pergunta, l.ordem);
        perguntasList.push(l.pergunta);
      }
    });
    perguntasList.sort((a, b) => ordemMap.get(a)! - ordemMap.get(b)!);

    const porPessoa = new Map<string, PessoaFolha>();
    linhas.forEach((l) => {
      const chave = `${l.participante}|${l.telefone ?? ''}|${l.participacao}|${l.grupo ?? ''}`;
      if (!porPessoa.has(chave)) {
        porPessoa.set(chave, {
          participante: l.participante,
          telefone: l.telefone,
          participacao: l.participacao,
          grupo: l.grupo,
          respostas: new Map(),
        });
      }
      porPessoa.get(chave)!.respostas.set(l.pergunta, l.resposta ?? '');
    });

    return { perguntas: perguntasList, pessoas: [...porPessoa.values()] };
  }, [linhas]);

  const dataGeracao = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  function baixarPlanilha() {
    const cabecalho = ['Participante', 'Telefone', 'Participação', 'Grupo', ...perguntas];
    const rows = pessoas.map((p) => [
      p.participante,
      p.telefone ?? '',
      p.participacao,
      p.grupo ?? '',
      ...perguntas.map((q) => p.respostas.get(q) ?? ''),
    ]);
    const slug = secao.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    baixarCSV(`${slug}.csv`, [[`Filtro: ${rotuloFiltro}`], [], cabecalho, ...rows]);
  }

  return (
    <div>
      <div className="nao-imprimir mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={aoVoltar}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-800 transition-colors hover:text-foreground-950"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Voltar
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-printer-line text-base" />
            Imprimir
          </button>
          <button
            type="button"
            onClick={baixarPlanilha}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-download-2-line text-base" />
            Baixar planilha
          </button>
        </div>
      </div>

      <div className="folha-imprimir overflow-x-auto rounded-lg border border-secondary-200 bg-white p-5">
        <div className="mb-4 border-b border-secondary-200 pb-4">
          <h2 className="text-lg font-medium text-foreground-950">{eventoNome}</h2>
          <p className="mt-0.5 text-sm text-secondary-600">{secao}</p>
          <p className="mt-0.5 text-xs text-foreground-700">Filtro: {rotuloFiltro}</p>
          <p className="mt-0.5 text-xs text-secondary-400">
            Gerado em {dataGeracao} · {pessoas.length} {pessoas.length === 1 ? 'pessoa' : 'pessoas'}
          </p>
        </div>

        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr>
              <th className="whitespace-nowrap border border-secondary-200 bg-background-50 px-2 py-2 text-xs font-medium text-secondary-600">
                Participante
              </th>
              <th className="whitespace-nowrap border border-secondary-200 bg-background-50 px-2 py-2 text-xs font-medium text-secondary-600">
                Telefone
              </th>
              <th className="whitespace-nowrap border border-secondary-200 bg-background-50 px-2 py-2 text-xs font-medium text-secondary-600">
                Participação
              </th>
              <th className="whitespace-nowrap border border-secondary-200 bg-background-50 px-2 py-2 text-xs font-medium text-secondary-600">
                Grupo
              </th>
              {perguntas.map((q) => (
                <th
                  key={q}
                  className="border border-secondary-200 bg-background-50 px-2 py-2 text-xs font-medium text-secondary-600"
                >
                  {q}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pessoas.map((p, i) => {
              const quebra = i % 25 === 24 && i !== pessoas.length - 1;
              return (
                <tr key={i} className={quebra ? 'folha-quebra' : ''}>
                  <td className="whitespace-nowrap border border-secondary-200 px-2 py-1.5 text-foreground-900">
                    {p.participante}
                  </td>
                  <td className="whitespace-nowrap border border-secondary-200 px-2 py-1.5 text-foreground-700">
                    {p.telefone ?? ''}
                  </td>
                  <td className="whitespace-nowrap border border-secondary-200 px-2 py-1.5 text-foreground-700">
                    {p.participacao}
                  </td>
                  <td className="whitespace-nowrap border border-secondary-200 px-2 py-1.5 text-foreground-700">
                    {p.grupo ?? ''}
                  </td>
                  {perguntas.map((q) => (
                    <td
                      key={q}
                      className="border border-secondary-200 px-2 py-1.5 text-foreground-700"
                    >
                      {p.respostas.get(q) ?? ''}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}